import { useState } from 'react';
import { Link } from 'react-router-dom';
import LoginModal from '../../../components/LoginModal';

export default function Hero() {
  const [showVideo, setShowVideo] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [loginType, setLoginType] = useState<'primary' | 'middle' | 'high' | 'teacher' | 'admin' | null>(null);
  const [showLoginMenu, setShowLoginMenu] = useState(false);

  return (
    <>
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://public.readdy.ai/ai/img_res/ae43bdf82e359f4403a3754e892960a6.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 via-blue-800/85 to-transparent"></div>
        </div>

        {/* Navigation */}
        <nav className="absolute top-0 left-0 right-0 z-20 bg-white/95 backdrop-blur-sm shadow-sm">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
                  <i className="ri-brain-line text-white text-xl"></i>
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">南湖区中小学AI通识教育平台</h1>
                  <p className="text-xs text-gray-500">Nanhu District K-12 AI Education Platform</p>
                </div>
              </div>
              <div className="hidden md:flex items-center space-x-8">
                <a href="#features" className="text-gray-700 hover:text-blue-600 transition-colors font-medium whitespace-nowrap cursor-pointer">核心功能</a>
                <a href="#segments" className="text-gray-700 hover:text-blue-600 transition-colors font-medium whitespace-nowrap cursor-pointer">学段入口</a>
                <a href="#architecture" className="text-gray-700 hover:text-blue-600 transition-colors font-medium whitespace-nowrap cursor-pointer">系统架构</a>
                <a href="#contact" className="text-gray-700 hover:text-blue-600 transition-colors font-medium whitespace-nowrap cursor-pointer">联系我们</a>
                <div className="relative">
                  <button 
                    onClick={() => setShowLoginMenu(!showLoginMenu)}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium whitespace-nowrap cursor-pointer flex items-center space-x-2"
                  >
                    <span>立即登录</span>
                    <i className={`ri-arrow-down-s-line transition-transform ${showLoginMenu ? 'rotate-180' : ''}`}></i>
                  </button>
                  {showLoginMenu && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-100 py-2 z-50">
                      <button
                        onClick={() => {
                          setLoginType('primary');
                          setShowLoginMenu(false);
                        }}
                        className="w-full px-4 py-3 text-left hover:bg-pink-50 transition-colors flex items-center space-x-3 cursor-pointer"
                      >
                        <i className="ri-user-smile-line text-pink-600"></i>
                        <span className="text-gray-700 font-medium">小学生登录</span>
                      </button>
                      <button
                        onClick={() => {
                          setLoginType('middle');
                          setShowLoginMenu(false);
                        }}
                        className="w-full px-4 py-3 text-left hover:bg-blue-50 transition-colors flex items-center space-x-3 cursor-pointer"
                      >
                        <i className="ri-user-line text-blue-600"></i>
                        <span className="text-gray-700 font-medium">初中生登录</span>
                      </button>
                      <div className="border-t border-gray-100 my-2"></div>
                      <button
                        onClick={() => {
                          setLoginType('teacher');
                          setShowLoginMenu(false);
                        }}
                        className="w-full px-4 py-3 text-left hover:bg-green-50 transition-colors flex items-center space-x-3 cursor-pointer"
                      >
                        <i className="ri-user-settings-line text-green-600"></i>
                        <span className="text-gray-700 font-medium">教师登录</span>
                      </button>
                      <button
                        onClick={() => {
                          setLoginType('admin');
                          setShowLoginMenu(false);
                        }}
                        className="w-full px-4 py-3 text-left hover:bg-indigo-50 transition-colors flex items-center space-x-3 cursor-pointer"
                      >
                        <i className="ri-admin-line text-indigo-600"></i>
                        <span className="text-gray-700 font-medium">管理员登录</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Content */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                <i className="ri-government-line text-yellow-300"></i>
                <span className="text-sm font-medium">依据《浙江省中小学人工智能通识教育指导意见》</span>
              </div>
              
              <h2 className="text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                开启智能时代
                <br />
                <span className="text-blue-300">AI素养培育新篇章</span>
              </h2>
              
              <p className="text-xl text-blue-100 mb-8 leading-relaxed">
                螺旋上升式课程体系 · 分学段差异化教学 · 全流程素养评价
                <br />
                为小学、初中、高中提供系统化人工智能通识教育解决方案
              </p>

              <div className="flex flex-wrap gap-4 mb-12">
                <button 
                  onClick={() => setShowVideo(true)}
                  className="px-8 py-4 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-all font-semibold flex items-center space-x-2 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-play-circle-line text-2xl"></i>
                  <span>观看演示视频</span>
                </button>
                <a 
                  href="#segments"
                  className="px-8 py-4 bg-blue-600/30 backdrop-blur-sm text-white rounded-lg hover:bg-blue-600/50 transition-all font-semibold border-2 border-white/30 whitespace-nowrap cursor-pointer"
                >
                  选择学段入口
                </a>
              </div>

              <div className="grid grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-4xl font-bold text-yellow-300 mb-2">15+</div>
                  <div className="text-sm text-blue-200">核心功能模块</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-yellow-300 mb-2">3</div>
                  <div className="text-sm text-blue-200">学段体系覆盖</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-yellow-300 mb-2">100%</div>
                  <div className="text-sm text-blue-200">政策合规保障</div>
                </div>
              </div>
            </div>

            <div className="hidden lg:block">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-blue-400 to-indigo-400 rounded-2xl blur-2xl opacity-30"></div>
                <img 
                  src="https://readdy.ai/api/search-image?query=Diverse%20group%20of%20Chinese%20students%20from%20elementary%20to%20high%20school%20using%20tablets%20and%20interactive%20displays%20with%20AI%20holographic%20interfaces%20colorful%20digital%20elements%20floating%20around%20them%20bright%20modern%20classroom%20setting%20clean%20white%20background%20professional%20educational%20photography%20inspiring%20and%20innovative%20atmosphere&width=800&height=900&seq=hero-students-002&orientation=portrait"
                  alt="学生使用AI学习平台"
                  className="relative rounded-2xl shadow-2xl object-cover w-full h-[600px]"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Video Modal */}
        {showVideo && (
          <div 
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6"
            onClick={() => setShowVideo(false)}
          >
            <div className="relative max-w-4xl w-full bg-white rounded-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <button 
                onClick={() => setShowVideo(false)}
                className="absolute top-4 right-4 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors z-10 cursor-pointer"
              >
                <i className="ri-close-line text-2xl text-gray-700"></i>
              </button>
              <div className="aspect-video bg-gray-900 flex items-center justify-center">
                <div className="text-center text-white">
                  <i className="ri-video-line text-6xl mb-4 opacity-50"></i>
                  <p className="text-lg">演示视频加载中...</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-white/50 rounded-full"></div>
          </div>
        </div>
      </section>

      <LoginModal
        isOpen={loginType !== null}
        onClose={() => setLoginType(null)}
        userType={loginType || 'primary'}
      />
    </>
  );
}
